/**
 * How the rooms move in play (view only).
 *
 * Soft and slow on purpose: a room is the background of a puzzle, and the
 * tickets in front of it have to stay the thing that moves the eye. Leaves sway
 * a few degrees, screens breathe, the lobby clock keeps the shift's time and
 * the train runs only when somebody is on it — so even the train says
 * something true about the level: people are about to arrive on this floor.
 *
 * Nothing here decides anything. The train reads when the sim will bring
 * people to the platform; it never brings them.
 */

/** Degrees a plant leans either way of upright. */
export const SWAY_DEGREES = 3;

/** How far a plant leans now, in degrees: slowly, each on its own rhythm. */
export function sway(ms: number, seed: number): number {
  const period = 3400 + seed * 1200;
  return Math.sin(((ms + seed * period) / period) * Math.PI * 2) * SWAY_DEGREES;
}

/** How bright a screen's glow is, 0..0.6: a slow breath, never a flicker. */
export function screenGlow(ms: number, seed: number): number {
  const period = 3600 + seed * 1600;
  return (Math.sin(((ms + seed * period) / period) * Math.PI * 2) + 1) * 0.3;
}

/** The working day the lobby clock shows across one shift: nine to five. */
export const DAY_STARTS = 9;
export const DAY_HOURS = 8;

/**
 * The lobby clock's hands, in degrees from twelve, `progress` (0..1) of the way
 * through the level's clock: nine o'clock as it starts, five as it runs out.
 */
export function clockHands(progress: number): { hour: number; minute: number } {
  const p = Math.min(1, Math.max(0, progress));
  const hours = DAY_STARTS + DAY_HOURS * p;
  return { hour: (hours % 12) * 30, minute: (hours % 1) * 360 };
}

/** The train's timetable around each arrival, in ms of the level's clock. */
export const TRAIN = {
  /** Running in, slowing to the platform. */
  arriveMs: 1300,
  /** At the platform before the first person steps off. */
  leadMs: 300,
  /** At the platform after the last one has. */
  dwellMs: 1000,
  /** Pulling out, gathering speed. */
  leaveMs: 1300,
} as const;

/**
 * Where the train is at `nowMs`: 0 at the platform, 1 gone past the wall.
 *
 * It stands at the platform from just before each arrival in `arrivals` (the
 * moments people appear on the floor) until a little after, and stays there
 * rather than leave and come straight back when arrivals are close together.
 * Eased: it slows into the platform and gathers speed out of it.
 */
export function trainAway(nowMs: number, arrivals: readonly number[]): number {
  const stands: [number, number][] = [];
  for (const at of [...arrivals].sort((a, b) => a - b)) {
    const start = at - TRAIN.leadMs;
    const end = at + TRAIN.dwellMs;
    const last = stands[stands.length - 1];
    if (last && start - last[1] < TRAIN.arriveMs + TRAIN.leaveMs) last[1] = Math.max(last[1], end);
    else stands.push([start, end]);
  }
  let away = 1;
  for (const [start, end] of stands) {
    if (nowMs >= start && nowMs <= end) return 0;
    if (nowMs > end) away = Math.min(away, (nowMs - end) / TRAIN.leaveMs);
    else away = Math.min(away, (start - nowMs) / TRAIN.arriveMs);
  }
  return away * away;
}
